// @ts-strict-ignore
import { Tooltip } from "@fullscript/aviary-web";
import type { SyntheticEvent } from "react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button, Link, Spacer, Typography } from "@aviary";
import { EmailInput } from "@shared/components/EmailInput/EmailInput";
import {
  isEmailValid,
  isEmailDotValid,
} from "@shared/components/EmailInput/EmailValidation/EmailValidation";
import { LoginOptions } from "@shared/components/LoginOptions/LoginOptions";
import { TermsCheckbox } from "@shared/components/TermsCheckbox/TermsCheckbox";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { PatientSignupEntryPoint } from "@shared/hooks/useAnalyticsLogger/PatientAnalyticsEntryPoint";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useFsAnalytics } from "@shared/hooks/useFsAnalytics/useFsAnalytics";
import { useNavigate, useLocation } from "@shared/react-router-dom/react-router-dom";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { FirstNameInput } from "@unauthenticated/shared/components/FirstNameInput/FirstNameInput";
import { FormWrapper } from "@unauthenticated/shared/components/FormWrapper/FormWrapper";
import { LastNameInput } from "@unauthenticated/shared/components/LastNameInput/LastNameInput";
import { IsNameValid } from "@unauthenticated/shared/components/NameValidation/NameValidation";
import { PasswordInput } from "@unauthenticated/shared/components/PasswordInput/PasswordInput";
import { isPasswordValid } from "@unauthenticated/shared/components/PasswordInput/PasswordValidation/PasswordValidation";
import { SSOErrorBox } from "@unauthenticated/shared/components/SSOErrorBox/SSOErrorBox";
import { useGlobalConfig } from "@unauthenticated/shared/context/GlobalConfig/GlobalConfig";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import {
  usePatientSignUpUnauthenticatedSharedMutation as usePatientSignUpMutation,
  type PatientSignUpUnauthenticatedSharedMutation as PatientSignUpMutationData,
} from "@unauthenticated/shared/data/mutations/PatientSignUp.mutation.generated";
import type { PatientAttributes } from "@unauthenticated/shared/data/types/PatientAttributes.d";
import { useInviteContext } from "@unauthenticated/shared/hooks/useInviteContext/useInviteContext";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import { ValuePropsCard } from "../ValuePropsCard/ValuePropsCard";

import * as styles from "./PatientStoreSignUpForm.styles";

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  pageVersion?: string;
  storeId?: string;
  isPreview?: boolean;
  ssoErrors?: string;
  preFilledEmail?: string;
  isSwiftFillSignIn?: boolean;
  isIntake?: boolean;
  requestedPractitionerId?: string;
  isStorefronts?: boolean;
}

const PatientStoreSignUpForm = ({
  patientAttributes,
  additionalAttributes,
  pageVersion,
  storeId,
  isPreview,
  ssoErrors,
  preFilledEmail = "",
  isSwiftFillSignIn,
  isIntake,
  requestedPractitionerId,
  isStorefronts,
}: Props) => {
  const { t } = useTranslation("common");
  const { emailRegex } = useGlobalConfig();
  const { storeSlug, treatmentPlanSlug, uuid, labRecommendationId } = patientAttributes;
  const SIGN_UP_PAGE = "rx_patient_sign_up_page";

  const navigate = useNavigate();
  const searchParams = useLocation().search;
  const logAnalyticsEvent = useAnalyticsLogger();
  const inviteContext = useInviteContext();
  const { logEvent } = useFsAnalytics();

  const [firstName, setFirstName] = useState("");
  const [firstNameDirty, setFirstNameDirty] = useState(false);
  const [lastName, setLastName] = useState("");
  const [lastNameDirty, setLastNameDirty] = useState(false);
  const [email, setEmail] = useState(isSwiftFillSignIn ? preFilledEmail : "");
  const [emailDirty, setEmailDirty] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordDirty, setPasswordDirty] = useState(false);
  const [tos, setTos] = useState(false);

  const [hasInteraction, setHasInteraction] = useState<boolean>(false);

  const [signUp, { loading, data }] = usePatientSignUpMutation({
    onCompleted: (payload: PatientSignUpMutationData) => {
      const errorMessage =
        payload?.auth?.patientSignUp?.errors?.message ||
        payload?.auth?.patientSignUp?.errors?.fields;
      if (!errorMessage) {
        if (inviteContext) {
          logAnalyticsEvent({
            eventName: AnalyticsEvent.PATIENT_INVITE_LINK_CONSENT_ACKNOWLEDGED,
            payload: {},
          });
        }
        window.location.assign(payload?.auth?.patientSignUp?.redirectPath ?? authRoutes.base);
      }
    },
  });

  const {
    firstName: firstNameError,
    lastName: lastNameError,
    email: emailError,
    password: passwordError,
  } = data?.auth?.patientSignUp?.errors?.fields ?? {};

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();

    gRecaptchaExecute(gToken => {
      // Carry the funnel attribution param (e.g. entry_point=UnAuthCart from the
      // unauth protocol/collection cart) through to patient.signup.signup_complete.
      const entryPointParam = new URLSearchParams(searchParams).get("entry_point");
      const entryPoint = Object.values(PatientSignupEntryPoint).includes(
        entryPointParam as PatientSignupEntryPoint
      )
        ? (entryPointParam as PatientSignupEntryPoint)
        : undefined;

      const optional = {
        ...additionalAttributes,
        redirectPath: isStorefronts
          ? authRoutes.continueGuestCheckoutSearchParams(searchParams)
          : (additionalAttributes?.redirectPath ?? undefined),
        ...(inviteContext && { inviteToken: inviteContext.inviteToken }),
        ...(entryPoint && { entryPoint }),
      };

      signUp({
        variables: {
          input: {
            attributes: {
              firstName,
              lastName,
              email,
              password,
              storeId,
              optional,
              intake: isIntake,
              requestedPractitionerId,
              tosAccepted: tos,
            },
            captchaToken: gToken,
          },
        },
      });
    });
  };

  const areFieldsValid = () => {
    const passwordValid = isPasswordValid(password);
    const firstNameValid = IsNameValid(firstName);
    const lastNameValid = IsNameValid(lastName);
    const emailValid = isEmailValid(email, emailRegex);

    return passwordValid && firstNameValid && lastNameValid && emailValid && tos;
  };

  const handleInputChange = (field: string, value: string) => {
    if (!hasInteraction) {
      logAnalyticsEvent({
        eventName: AnalyticsEvent.CREATE_PATIENT_START,
        payload: {
          referrer: window.location.href,
        },
      });
      logEvent().authSignupStart({ form_version: pageVersion });
      logEvent().patientCreatePatientStart({});
      setHasInteraction(true);
    }

    switch (field) {
      case "firstName":
        setFirstName(value);
        break;
      case "lastName":
        setLastName(value);
        break;
      case "email":
        setEmail(value);
        break;
      case "password":
        setPassword(value);
        break;
      default:
        break;
    }
  };

  const handleTosChange = (value: boolean) => {
    if (!hasInteraction) {
      logAnalyticsEvent({
        eventName: AnalyticsEvent.CREATE_PATIENT_START,
        payload: {
          referrer: window.location.href,
        },
      });
      logEvent().authSignupStart({ form_version: pageVersion });
      logEvent().patientCreatePatientStart({});
      setHasInteraction(true);
    }
    setTos(value);
  };

  const handleEmailInputBlur = e => {
    setEmailDirty(true);
    if (e?.target.value) {
      setEmail(e.target.value);
    }
  };

  const getEmailError = () => {
    if (!isEmailDotValid(email)) {
      return t(l.common.EmailMustHaveDot);
    }
    if (!emailRegex.test(email)) {
      return t(l.common.EmailInvalidFormat);
    }
  };

  const handleOnSignInClick = () => {
    navigate(`${authRoutes.welcomeStart(storeSlug, uuid ?? treatmentPlanSlug)}${searchParams}`);
  };

  const getPasswordError = () => {
    if (passwordError) return passwordError;
    const { message } = data?.auth?.patientSignUp?.errors ?? {};
    if (message?.includes("in use")) {
      const forgotLink = `${authRoutes.forgot_password}`;
      const newErrorMessage = (
        <Trans i18nKey={l.common.emailInUse}>
          This email is already in use. Try
          <Link onClick={handleOnSignInClick}>sign in</Link>
          or
          <Link href={forgotLink}>forgot password</Link>
          instead
        </Trans>
      );

      return [newErrorMessage];
    }
    if (message) return [message];

    return null;
  };

  return (
    <FormWrapper css={styles.formWrapper(isStorefronts)}>
      <SSOErrorBox ssoErrors={ssoErrors} />
      <div css={styles.buttonWrapper}>
        <LoginOptions
          commonSSOProps={{
            signUpPage: SIGN_UP_PAGE,
            pageVersion,
            disabled: isPreview,
            treatmentPlanSlug,
            labRecommendationId,
            storeSlug,
            uuid,
            additionalAttributes,
            buttonStyles: isStorefronts ? styles.buttonStyles : undefined,
          }}
          isStorefronts
        />
      </div>
      <fieldset disabled={isPreview} css={styles.fieldset}>
        <form
          onSubmit={handleSubmit}
          css={styles.form}
          noValidate
          data-e2e="patient-sign-up-form"
          data-gtm-id="patient-sign-up-form"
        >
          <div css={styles.Row}>
            <FirstNameInput
              value={firstName}
              onChange={e => handleInputChange("firstName", e.target.value)}
              wrapperStyles={styles.margin}
              required
              isDirty={firstNameDirty}
              onBlur={() => {
                setFirstNameDirty(true);
              }}
              errors={firstNameError}
              isLoading={loading}
            />
            <LastNameInput
              value={lastName}
              onChange={e => handleInputChange("lastName", e.target.value)}
              required
              isDirty={lastNameDirty}
              onBlur={() => {
                setLastNameDirty(true);
              }}
              isLoading={loading}
              errors={lastNameError}
            />
          </div>
          <div css={styles.Row}>
            <EmailInput
              value={email}
              disabled={!!isSwiftFillSignIn && !!preFilledEmail}
              onChange={e => handleInputChange("email", e.target.value)}
              setEmail={setEmail}
              required
              isDirty={emailDirty}
              onBlur={handleEmailInputBlur}
              isLoading={loading}
              error={getEmailError() ?? emailError?.[0]}
              hideSuggestion
            />
          </div>
          <div css={styles.Row}>
            <PasswordInput
              value={password}
              onChange={e => handleInputChange("password", e.target.value)}
              required
              isDirty={passwordDirty}
              onBlur={() => setPasswordDirty(true)}
              isLoading={loading}
              errors={getPasswordError()}
            />
          </div>
          {!!isStorefronts && (
            <>
              <Spacer height="half" />
              <ValuePropsCard />
              <Spacer height="oneQuarter" />
            </>
          )}
          <div css={styles.Row}>
            <TermsCheckbox onChange={() => handleTosChange(!tos)} checked={tos} />
          </div>
          <div css={styles.marginBottom}>
            <Tooltip tooltipContent={t(l.common.SubmitButtonTooltip)} isTooltipHidden={tos}>
              <Button
                data-e2e="intake-create-account-button"
                type="submit"
                disabled={!areFieldsValid()}
                isLoading={loading}
              >
                {t(l.common.CreateMyAccount)}
              </Button>
            </Tooltip>
          </div>
        </form>
      </fieldset>
      {!isStorefronts && (
        <Typography sizeOverride="footnote">
          <Link data-e2e="intake-back-link" onClick={() => navigate(-1)}>
            {t(l.auth.back)}
          </Link>
        </Typography>
      )}
    </FormWrapper>
  );
};

export { PatientStoreSignUpForm };
